import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { DatePicker } from "@/components/atoms/DatePicker"; 
import Select from "@/components/molecules/Select";
import Button from "@/components/atoms/Button";
import { Account } from "@/types/Account";
import { useState } from "react";

interface ReportModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: Account[];
  onCreateReport: (reportFilters: { account_ids: string[]; start_date: Date; end_date: Date }) => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onOpenChange, accounts, onCreateReport }) => {
  const [selectedAccount, setSelectedAccount] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const handleCreateReport = () => {
    if (selectedAccount && startDate && endDate) {
      onCreateReport({ account_ids: selectedAccount, start_date: startDate, end_date: endDate });

      setSelectedAccount([]);
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedAccount([]);
      setStartDate(undefined);
      setEndDate(undefined);
    }

    onOpenChange(open);
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/50 dark:bg-black/60 z-40 transition-opacity" />
      <Dialog.Content className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-background p-6 rounded-lg shadow-lg max-w-md w-full transition-colors">
          <Dialog.Title className="text-lg font-bold">Create Report</Dialog.Title>
          <Dialog.Description className="text-sm text-gray-500 mb-4">
            Select an account and date range to generate the report.
          </Dialog.Description>

          <Select
            multiple
            options={[
              { value: "all", label: "All Accounts" },
              ...accounts.map((account) => ({
                value: account.id.toString(),
                label: account.name,
                disabled: selectedAccount.includes("all"),
              })),
            ]}
            value={selectedAccount}
            onChange={(newSelection) => {
              if (newSelection.includes("all")) {
                setSelectedAccount(["all"]);
              } else {
                setSelectedAccount(newSelection.filter((item) => item !== "all"));
              }
            }}
            className= 'w-100'
            enabledSearch={true}
            placeholder="Select Account"
          />

          <DatePicker placeholder="SOW Start Period" value={startDate} onChange={setStartDate} mode="month" monthSelectionType="start" />

          <DatePicker placeholder="SOW End Period" value={endDate} onChange={setEndDate} mode="month" monthSelectionType="end" />


          <div className="flex justify-end mt-4 space-x-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateReport}>Create Report</Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-black"
              aria-label="Close"
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReportModal;